import { computed, makeObservable } from 'mobx'

import { Collection } from 'modules/core/lib/mobx-rest'
import User, { IUser } from 'modules/core/store/models/User'

import type { Employees } from 'modules/employees/store/collections/employees'
// FIXME: Core must not be coupled with other modules.
import type Employee from 'modules/employees/store/models/Employee'
import type QuestionGroup from 'modules/surveys/store/models/QuestionGroup'

export class Users extends Collection<User> {
  constructor(data = []) {
    super(data)
    makeObservable(this, {
      current: computed,
    })
  }

  get cacheKey(): string {
    return 'users'
  }

  get indexes() {
    return ['current', 'email']
  }

  /**
   * @deprecated Before using this method, consider using the `useCurrentSession` hook since
   * we are trying to move away from using mobx-rest and use GraphQL instead.
   */
  get current(): User {
    const user = this.find({
      current: true,
    })

    if (!user) {
      throw Error('Error finding users with current = true')
    }

    return user
  }

  terminated(employees: Employees): User[] {
    return this.models.filter((user) => {
      const employee = user.isNew
        ? undefined
        : employees.find({
            access_id: user.attributes.id,
          })
      return user.isTerminated(employee)
    })
  }

  notTerminated(employees: Employees): User[] {
    return this.models.filter((user) => {
      const employee = user.isNew
        ? undefined
        : employees.find({
            access_id: user.attributes.id,
          })
      return !user.isTerminated(employee)
    })
  }

  notTerminatedExcludingExternals(employees: Employees): User[] {
    return this.models.filter((user) => {
      const employee = user.isNew
        ? undefined
        : employees.find({
            access_id: user.attributes.id,
          })
      if (user.isTerminated(employee)) return false
      return !!employee
    })
  }

  async findOrCreate(attributes: Partial<IUser>): Promise<IUser> {
    const existingUser = this.find({ email: attributes.email })

    if (existingUser) {
      return existingUser.toJS()
    }

    return await this.create(attributes)
  }

  model() {
    return User
  }
}

const singleton: Users = new Users()

/**
 * @deprecated Use the inline implementation instead:
 * ```
 * const userId = employee.attributes.access_id
 * const user = users.get(userId)
 * if (!user) {
 *   throw Error(`Error getting users with id = ${userId}`)
 * }
 * ```
 */
export const employeeUser = (employee: Employee) => {
  const userId = employee.attributes.access_id
  const user = singleton.get(userId)

  if (!user) {
    throw Error(`Error getting users with id = ${userId}`)
  }

  return user
}

export const questionGroupRespondent = (questionGroup: QuestionGroup) => {
  return singleton.get(questionGroup.attributes.respondent_id)
}
export default singleton
